import { LibraryNameInput } from '@components/LibraryNameInput/LibraryNameInput';
import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import { formatLibraryCardMeta } from '@lib/utils/libraryStats';
import { ActionButton } from '@pages/ActionButton';
import { BaseModal } from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { useModalRegistration } from '@stores/ModalStore';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { CustomWordHintsToggle } from './CustomWordHintsToggle';
import { LibraryCard } from './LibraryCard';
import shell from './LibraryModalShell.module.scss';
import css from './LibraryRenameModal.module.scss';
import { LibraryThumbnailGrid } from './LibraryThumbnailGrid';
import { useLibraryEditForm } from './useLibraryEditForm';

export type LibraryRenameModalRef = {
  open: () => void;
  close: () => void;
};

export const LibraryRenameModal = observer(
  forwardRef<
    LibraryRenameModalRef,
    {
      libraryId: string;
      currentName: string;
    }
  >(({ libraryId, currentName }, ref) => {
    const [open, setOpen] = useState(false);
    const [hoveredMediaId, setHoveredMediaId] = useState<string | null>(null);
    const gridRef = useRef<any>(null);
    const {
      library,
      videoFiles,
      newName,
      setNewName,
      newThumbnailMediaId,
      setNewThumbnailMediaId,
      glossary,
      setGlossary,
      useWordHints,
      setUseWordHints,
      isSavingChanges,
      changes,
      seed,
      reset,
      submit,
    } = useLibraryEditForm(libraryId, currentName);
    const autoFocusOnDesktop = useDesktopAutoFocus();

    // `open && !!library` rather than `open` alone: this renders null while the library is
    // falsy, so `open` on its own does not mean "visible". Keyed rather than registered from
    // onOpen, so it also stays correct if the library disappears mid-open — which the old
    // imperative guard below could not express.
    useModalRegistration('library-rename', open && !!library);

    const onOpen = () => {
      // Still guard the seed/setOpen: opening against a library that hasn't loaded would show
      // an empty form.
      if (!library) return;
      seed();
      setOpen(true);
    };
    const onClose = () => {
      reset();
      setOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (await submit()) {
        onClose();
      }
    };

    useImperativeHandle(ref, () => ({
      open: onOpen,
      close: onClose,
    }));

    if (!library) {
      return null;
    }

    // The preview below is a live "how the card will look" — so its meta lines come from the
    // grid's own builder rather than being written out again here, which is how this one drifted
    // to a bare media count while the real card grew an owner line.
    const cardMeta = formatLibraryCardMeta(library);

    return (
      <BaseModal
        open={open}
        onClose={onClose}
        className={shell.modal}
        title="Edit library"
        subtitle="Change its name, thumbnail and word hints."
      >
        <form className={shell.form} onSubmit={handleSubmit}>
          <div className={shell.field}>
            <label htmlFor="library-name" className={shell.fieldLabel}>
              Name
            </label>
            <LibraryNameInput
              id="library-name"
              placeholder={currentName}
              value={newName}
              onValueChange={setNewName}
              onFocus={(e) => e.target.select()}
              disabled={isSavingChanges}
              autoFocus={autoFocusOnDesktop}
            />
          </div>
          <div className={shell.field}>
            <span className={shell.fieldLabel}>Thumbnail</span>
            <div className={css.thumbnailChooser}>
              <div className={css.cardPreview}>
                <LibraryCard
                  id={libraryId}
                  name={newName}
                  mediaCount={cardMeta.mediaCount ?? undefined}
                  folderCount={cardMeta.folderCount}
                  ownerLine={cardMeta.owner ?? undefined}
                  library={library}
                  hideOptions={true}
                  thumbnailMediaId={newThumbnailMediaId}
                  noLink
                  hoveredMediaId={hoveredMediaId}
                />
              </div>
              {videoFiles.length > 0 ? (
                <LibraryThumbnailGrid
                  libraryId={libraryId}
                  folderFiles={videoFiles}
                  selectedMediaId={newThumbnailMediaId}
                  onSelectMedia={setNewThumbnailMediaId}
                  gridRef={gridRef}
                  onHoverChange={setHoveredMediaId}
                />
              ) : (
                <p className={css.noThumbnails}>
                  Add video or image media to this library to pick a thumbnail.
                </p>
              )}
            </div>
          </div>
          <div className={shell.options}>
            <CustomWordHintsToggle
              checked={useWordHints}
              onChange={setUseWordHints}
              words={glossary}
              onWordsChange={setGlossary}
              showAppliesToNewMediaWarning={(library.stats?.count ?? 0) > 0}
              disabled={isSavingChanges}
            />
          </div>
          <div className={shell.actions}>
            <ActionButton
              variant="secondary"
              onClick={onClose}
              type="button"
              disabled={isSavingChanges}
            >
              Cancel
            </ActionButton>
            <ActionButton type="submit" disabled={isSavingChanges || !changes.hasChanges}>
              {isSavingChanges ? 'Saving…' : 'Save changes'}
            </ActionButton>
          </div>
        </form>
      </BaseModal>
    );
  }),
);
